<script setup lang="ts">
import { useProductSearch } from '@shopware-pwa/composables-next'
import { CustomFields } from '~/composables/useCrossSellingProducts'
import { getCategoryPermalinkByForeignKey, getShopPermalink } from '~/lib'
import { useSchemaProduct } from '~/composables/useSchemaProduct'

const props = defineProps<{
  foreignKey: string
}>()

const { search } = useProductSearch()
const { getVariants, getCrossSellingProducts } = useCrossSellingProducts()
const { t } = useI18n()
const localePath = useLocalePath()
const { getFormattedPrice } = usePrice()
const router = useRouter()

const { data: productResponse } = await useAsyncData('cmsProduct' + props.foreignKey, async () => {
  const productResponse = await search(props.foreignKey, {
    withCmsAssociations: false,
  })
  return productResponse
})
const { product } = useProduct(productResponse.value?.product, productResponse.value?.configurator)

const customFields = product.value?.translated.customFields as unknown as CustomFields

const subtitle = customFields?.custom_product_fields_subtitle

const productDetails = [
  {
    label: t('shop.productPage.productDetails.productNumber'),
    value: product.value?.productNumber,
  },
  // {
  //   label: t('shop.productPage.productDetails.edition'),
  //   value: customFields?.custom_product_fields_edition,
  // },
  {
    label: t('shop.productPage.productDetails.editionDetails'),
    value: customFields?.custom_product_fields_edition_statement,
  },
  // {
  //   label: t('shop.productPage.productDetails.binding'),
  //   value: customFields?.custom_product_fields_binding,
  // },
  {
    label: t('shop.productPage.productDetails.weight'),
    value: customFields?.custom_product_fields_weight,
  },
  {
    label: t('shop.productPage.productDetails.pageCount'),
    value: customFields?.custom_product_fields_page_count,
  },
  {
    label: t('shop.productPage.productDetails.dimensions'),
    value: customFields?.custom_product_fields_dimensions,
  },
  {
    label: t('shop.productPage.productDetails.isbn'),
    value: product.value?.ean,
  },
  {
    label: t('shop.productPage.productDetails.editor'),
    value: customFields?.custom_product_fields_editor,
  },
  {
    label: t('shop.productPage.productDetails.publisher'),
    value: customFields?.custom_product_fields_publisher,
  },
].filter((item) => item.value !== undefined)

const productDescription = product.value?.description ?? ''

const variants = await getVariants(product.value, customFields)

const crossSellingProducts = await getCrossSellingProducts(product.value)

const backUrl = computed(() => {
  return router.options.history.state.back ? router.options.history.state.back : localePath(getShopPermalink())
})

const links = computed(() => ({
  update: customFields?.custom_product_fields_update_link,
  sample: customFields?.custom_product_fields_other_text,
}))

useShopwareMetaTags(product.value)

// Return product type based on the variants
const productType = computed(() => {
  return variants.value?.find((variant) => variant.id === product.value?.id)?.type
})

// Add schema.org JSON-LD structured data
useSchemaProduct(product, productType)
</script>
<template>
  <div class="grid container">
    <div class="tl-6 image-outer-wrapper">
      <div v-animate="{ animation: 'vertical-reveal' }" class="image-wrapper">
        <img
          v-if="product?.cover?.media?.url"
          :src="product?.cover?.media?.url"
          :alt="product?.cover?.media?.alt || product?.translated?.name || 'product image'"
          :title="product?.cover?.media?.title || product?.translated?.name || 'product image'"
        />
      </div>
    </div>
    <div class="tl-5 tl-o-1">
      <ButtonBack v-animate="{ animation: 'vertical-reveal' }" :to="backUrl">{{
        $t('shop.productPage.back')
      }}</ButtonBack>
      <ShopProductLabels :product="product" />
      <div class="title-wrapper">
        <h1 v-animate="{ animation: 'vertical-reveal' }" class="text-xlarge">{{ product.translated.name }}</h1>
        <span v-if="subtitle" v-animate="{ animation: 'vertical-reveal' }" class="subtitle text-small">{{
          subtitle
        }}</span>
      </div>

      <ShopProductFeatures :product="product" />

      <div v-if="crossSellingProducts?.relatedProductsReplaced?.length" class="replacement-product">
        <h4 v-animate="{ animation: 'vertical-reveal' }" class="text-small">
          {{ $t('shop.productPage.relatedProductsReplaced.title') }}
        </h4>
        <ShopProductTeaserSmall
          v-for="relatedProduct in crossSellingProducts?.relatedProductsReplaced"
          :key="relatedProduct.id"
          v-animate="{ animation: 'vertical-reveal' }"
          :product="relatedProduct"
        />
      </div>

      <ul class="cul products-list">
        <li v-for="variant in variants" :key="variant.id" v-animate="{ animation: 'vertical-reveal' }">
          <div>
            <NuxtLink :to="variant.url">
              <ShopProductVariant class="variant" :active="variant.id === product.id">
                <Icon v-if="variant.type === 'e-book'" class="variant-icon" name="ebook" />
                <Icon v-if="variant.type === 'print'" class="variant-icon" name="book" />
                <Icon v-if="variant.type === 'learning-card'" class="variant-icon" name="showcases" />
                <Icon v-if="variant.type === 'combi'" class="variant-icon" name="combo" />
                <Icon v-if="variant.type === 'set'" class="variant-icon" name="set" />
                <span class="variant-name label">{{ variant.name }}</span>
                <span class="variant-price f-13 f-medium">{{ variant.price }}</span>
              </ShopProductVariant>
            </NuxtLink>
          </div>
        </li>
      </ul>

      <ul class="cul products-list">
        <li
          v-for="languageVariationProduct in crossSellingProducts?.languageVariations"
          :key="languageVariationProduct.id"
          v-animate="{ animation: 'vertical-reveal' }"
        >
          <NuxtLink :to="languageVariationProduct.slug">
            <Tag :name="languageVariationProduct.label" :active="languageVariationProduct.id === product.id" />
          </NuxtLink>
        </li>
      </ul>

      <div v-animate="{ animation: 'vertical-reveal' }" class="add-to-cart-wrapper">
        <div class="price f-24">
          {{ getFormattedPrice(product.calculatedPrice.totalPrice) }}
          <span v-if="product.calculatedPrice.listPrice" class="text-small">
            {{ $t('shop.listingPage.price.insteadOf') }}
            {{ getFormattedPrice(product.calculatedPrice.listPrice.price) }}
          </span>
        </div>
        <ShopAddToCartButton :product="product" :label="$t('shop.productPage.buyButton.label')" />
      </div>

      <div class="description text-small">
        <div v-animate="{ animation: 'vertical-reveal' }" v-html="productDescription" />
        <NuxtLink v-if="links.update" :to="links.update" class="link-default">
          {{ $t('shop.productPage.links.update') }}
        </NuxtLink>
        <NuxtLink v-if="links.sample" :to="links.sample" class="link-default">
          {{ $t('shop.productPage.links.sample') }}
        </NuxtLink>
      </div>

      <div class="specs text-small">
        <div v-for="(detail, index) in productDetails" :key="index" v-animate="{ animation: 'vertical-reveal' }">
          <div class="spec f-bold">{{ detail.label }}</div>
          <div class="value">{{ detail.value }}</div>
        </div>
      </div>
    </div>

    <div v-if="crossSellingProducts?.productSetProducts?.length" class="cross-selling-products">
      <h2 class="text-xlarge">{{ $t('shop.productPage.productSetProducts.title') }}</h2>
      <Products :products="crossSellingProducts?.productSetProducts" />
    </div>

    <div v-if="crossSellingProducts?.containedProducts?.length" class="cross-selling-products">
      <h2 class="text-xlarge">{{ $t('shop.productPage.containedProducts.title') }}</h2>
      <Products :products="crossSellingProducts?.containedProducts" />
    </div>

    <div v-if="crossSellingProducts?.relatedProducts?.length" class="cross-selling-products">
      <h2 class="text-xlarge">{{ $t('shop.productPage.relatedProducts.title') }}</h2>
      <Products :products="crossSellingProducts?.relatedProducts" />
    </div>
  </div>
</template>
<style scoped lang="scss">
.image-outer-wrapper {
  text-align: center;
}
.labels {
  margin-bottom: 16px;
}
.features {
  margin-bottom: 48px;
}
.image-wrapper {
  display: inline-block;
  background-color: var(--c-background-medium);
  padding: 60px 40px;
  @include breakpoint(ds) {
    padding: 180px 0;
    width: 100%;
  }
}
:deep(.button.back) {
  padding: 0;
  margin: 24px 0;
  @include breakpoint(ds) {
    margin: 48px 0 24px 0;
  }
}
.title-wrapper {
  margin-bottom: 48px;
  h1 {
    margin-bottom: 16px;
  }
}

.replacement-product {
  margin-bottom: 32px;
  @include breakpoint(tl) {
    margin-bottom: 48px;
  }
}

.variant {
  :deep(.text) {
    display: flex;
    flex-direction: column;
    align-items: center;
    span {
      display: block;
      width: auto;
      height: auto;
    }
  }

  &-icon {
    margin-bottom: 8px;
  }
  &-price {
    opacity: 0.75;
  }
}
.products-list {
  margin-bottom: 24px;
  display: flex;
  gap: 20px 4px;
  flex-wrap: wrap;
}
.add-to-cart-wrapper {
  margin-bottom: 40px;
  @include breakpoint(t) {
    margin-bottom: 80px;
  }
  @include breakpoint(tl) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: auto;
  }
  .price {
    display: grid;
    font-weight: bold;
    color: var(--c-primary);
    margin-bottom: 16px;
    @include breakpoint(tl) {
      margin-bottom: 0;
    }
    span {
      font-weight: normal;
      text-decoration: line-through;
      color: var(--c-gray-75);
    }
  }
}
.description {
  color: var(--c-gray-75);
  margin-bottom: 40px;
  @include breakpoint(t) {
    margin-bottom: 80px;
  }
}
.specs {
  margin-bottom: 40px;
  @include breakpoint(t) {
    margin-bottom: 80px;
  }
  & > div {
    padding: 17px 0;
    border-bottom: 1px solid var(--c-line-light);
    display: grid;
    grid-template-columns: 2fr 4fr;
    gap: 10px;
  }
  .value {
    color: var(--c-gray-75);
  }
}
.cross-selling-products {
  margin-bottom: 40px;
  @include breakpoint(t) {
    margin-bottom: 160px;
  }
}
</style>
